import type { ReactNode } from 'react';

import { WidgetSitePanelSaveHandler } from '@/components/widgetConfigSiteTabbedShell/widgetConfigSiteTabbedShell/widgetConfigSiteTabbedShell.types.ts';
import { SiteProvider } from '@/providers/appCoreProvider/accessibleSitesProvider/SiteProvider.tsx';
import { useSiteContext } from '@/providers/appCoreProvider/accessibleSitesProvider/siteProvider/SiteContext.ts';
import { InventorySiteSection, WidgetConfigModalProps } from '@/types.ts';

import { WidgetSitePanelProvider } from './widgetConfigSitePanel/WidgetSitePanelProvider.tsx';

type SitePanelContentProps = {
  section: InventorySiteSection;
  onSave: WidgetConfigModalProps['onSave'];
  registerSaveForSite: (siteKey: string, save: WidgetSitePanelSaveHandler) => () => void;
  children: ReactNode;
};

const SitePanelContent = ({
  section,
  onSave,
  registerSaveForSite,
  children,
}: SitePanelContentProps) => {
  const { currentSite } = useSiteContext();
  if (!currentSite) return null;

  const siteScopedOnSave: WidgetConfigModalProps['onSave'] = props => onSave(props, currentSite);

  return (
    <WidgetSitePanelProvider
      section={section}
      onSave={siteScopedOnSave}
      registerSave={save => registerSaveForSite(section.siteKey, save)}
    >
      {children}
    </WidgetSitePanelProvider>
  );
};

type WidgetConfigSitePanelProps = {
  section: InventorySiteSection;
  onSave: WidgetConfigModalProps['onSave'];
  registerSaveForSite: (siteKey: string, save: WidgetSitePanelSaveHandler) => () => void;
  children: ReactNode;
};

/** Wraps tab panel content in {@link SiteProvider} and per-panel shell context. */
export const WidgetConfigSitePanel = ({
  section,
  onSave,
  registerSaveForSite,
  children,
}: WidgetConfigSitePanelProps) => (
  <SiteProvider persist siteId={section.siteId}>
    <SitePanelContent section={section} onSave={onSave} registerSaveForSite={registerSaveForSite}>
      {children}
    </SitePanelContent>
  </SiteProvider>
);
